import { computed } from 'mobx';
import { CXContainerableBlock } from '~/components/ClientExperience/OneFlow/models/cx-containerable-block.model';
import {
    IContainerableBlockResponse,
    ISignaturesBlockResponse
} from '~/types/server.types';
import type { CXRootStore } from '~/components/ClientExperience/stores/cx-root.store';
import type { ContainerableBlock } from '~/models/blocks/containerable-block.model';
import { IChildLayout } from '~/types/model.types';
import { SignaturesBlock } from '~/models/blocks/signatures-block.model';
import { ContractBlockErrors } from '~/types/error.types';
import type { CXSignatureBlock } from '~/components/ClientExperience/OneFlow/models/cx-signature-block.model';
import type { FlowRevision } from '~/models/flow-revision.model';

export class CXSignaturesBlock extends CXContainerableBlock {
    type: 'signatures_block';

    constructor(
        block: ISignaturesBlockResponse | SignaturesBlock,
        flowRevision: FlowRevision<CXRootStore>,
        rootStore: CXRootStore
    ) {
        super(
            block as IContainerableBlockResponse | ContainerableBlock,
            flowRevision,
            rootStore
        );
        this.type = block.type;

        if (block instanceof SignaturesBlock) {
            this.children = (<SignaturesBlock>block).children;
        } else {
            this.children = block.children as IChildLayout[];
        }

        this.children = this.children || [];
    }

    @computed
    get blockInputValidations() {
        const {
            userStore: { currUser }
        } = this.rootStore;
        const validations = {};
        const unSignedSignatures = this.childBlocks.filter(child => {
            const signatureBlock = child as CXSignatureBlock;
            return (
                signatureBlock.signature.signee_id === currUser?._id &&
                !signatureBlock.isSigned
            );
        }).length;

        const invalidatedSignatures = this.childBlocks.filter(child => {
            const signatureBlock = child as CXSignatureBlock;
            return (
                signatureBlock.signature.signee_id === currUser?._id &&
                signatureBlock.connectedVariable?.invalidated_on
            );
        }).length;

        if (unSignedSignatures) {
            validations[ContractBlockErrors.UnSignedSignature] =
                unSignedSignatures;
        }

        if (invalidatedSignatures) {
            validations[ContractBlockErrors.InvalidatedSignature] =
                invalidatedSignatures;
        }

        return validations;
    }

    @computed
    get unsignedRequiredClientSignatures() {
        return (this.childBlocks as CXSignatureBlock[]).filter(
            child =>
                child?.missingRequiredSignature &&
                child?.signature.signee_type !== 'vendor'
        );
    }

    @computed
    get unsignedRequiredCurrUserSignature() {
        // should this better be named unsignedRequiredCurrClientSignature ? or is it obvious since its in cx
        const currUserId = this.rootStore.userStore.currUser._id;
        return this.unsignedRequiredClientSignatures.find(
            signatureBlock => signatureBlock.signeeId === currUserId
        );
    }

    @computed
    get unsignedRequiredOtherClientSignatures() {
        const currUserId = this.rootStore.userStore.currUser._id;
        return this.unsignedRequiredClientSignatures.filter(
            signatureBlock => signatureBlock.signeeId !== currUserId
        );
    }
}
